/**
 * UnifiedDashboardHero — iter-390
 * ===============================
 * A single, calm, LIGHT, industry-themed dashboard header that replaces three
 * previously-stacked, redundant blocks (WelcomeHero dark box + DashboardHeader
 * + the AIIntelligenceHero greeting strip).
 *
 * Rules (per user + design blueprint):
 *  - Greeting headline is ALWAYS "Good {morning|afternoon|evening}, {firstName}"
 *    — the person's name, never their role.
 *  - The business name sits UNDERNEATH the greeting (secondary).
 *  - Colour is driven by the INDUSTRY field (a small finite palette, light/soft
 *    washes — never dark/confronting). Unknown industries fall back to slate.
 *  - No hardcoded platform branding inside the box — business name comes from data.
 *  - Dashboard tabs render directly UNDERNEATH this hero (in the parent), not
 *    inside the coloured surface.
 */
import React, { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ArrowUpRight, Sunrise, Sun, Sunset, RefreshCcw, Plus, Sparkles, Wifi, WifiOff,
} from 'lucide-react';
import { getIndustryConfig } from '@/config/dashboardConfig';
import { resolveGreetingName } from '@/utils/personName';
import { DashboardCustomizationTrigger } from './DashboardCustomizationPanel';
import NotificationCenter from '@/components/NotificationCenter';

// Light, positive, industry-driven palette (design blueprint iter-390).
const INDUSTRY_THEME = {
  hospitality:   { bg: 'bg-orange-50',  ring: 'ring-orange-200/60',  text: 'text-orange-950',  chip: 'bg-orange-100/60',  cta: 'bg-orange-600 hover:bg-orange-700' },
  qsr:           { bg: 'bg-[linear-gradient(135deg,#3d0a12_0%,#7a0f24_42%,#b3122f_78%,#d61f42_100%)]', ring: 'ring-white/15', text: 'text-white', chip: 'bg-white/12', cta: 'bg-white/15 hover:bg-white/25 backdrop-blur', dark: true },
  retail:        { bg: 'bg-indigo-50',  ring: 'ring-indigo-200/60',  text: 'text-indigo-950',  chip: 'bg-indigo-100/60',  cta: 'bg-indigo-600 hover:bg-indigo-700' },
  healthcare:    { bg: 'bg-rose-50',    ring: 'ring-rose-200/60',    text: 'text-rose-950',    chip: 'bg-rose-100/60',    cta: 'bg-rose-600 hover:bg-rose-700' },
  professional:  { bg: 'bg-purple-50',  ring: 'ring-purple-200/60',  text: 'text-purple-950',  chip: 'bg-purple-100/60',  cta: 'bg-purple-600 hover:bg-purple-700' },
  fitness:       { bg: 'bg-emerald-50', ring: 'ring-emerald-200/60', text: 'text-emerald-950', chip: 'bg-emerald-100/60', cta: 'bg-emerald-600 hover:bg-emerald-700' },
  technology:    { bg: 'bg-blue-50',    ring: 'ring-blue-200/60',    text: 'text-blue-950',    chip: 'bg-blue-100/60',    cta: 'bg-blue-600 hover:bg-blue-700' },
  beauty:        { bg: 'bg-pink-50',    ring: 'ring-pink-200/60',    text: 'text-pink-950',    chip: 'bg-pink-100/60',    cta: 'bg-pink-600 hover:bg-pink-700' },
  construction:  { bg: 'bg-amber-50',   ring: 'ring-amber-200/60',   text: 'text-amber-950',   chip: 'bg-amber-100/60',   cta: 'bg-amber-600 hover:bg-amber-700' },
  automotive:    { bg: 'bg-red-50',     ring: 'ring-red-200/60',     text: 'text-red-950',     chip: 'bg-red-100/60',     cta: 'bg-red-600 hover:bg-red-700' },
  education:     { bg: 'bg-violet-50',  ring: 'ring-violet-200/60',  text: 'text-violet-950',  chip: 'bg-violet-100/60',  cta: 'bg-violet-600 hover:bg-violet-700' },
  manufacturing: { bg: 'bg-slate-50',   ring: 'ring-slate-200/60',   text: 'text-slate-900',   chip: 'bg-slate-100/70',   cta: 'bg-slate-800 hover:bg-slate-900' },
  property:      { bg: 'bg-teal-50',    ring: 'ring-teal-200/60',    text: 'text-teal-950',    chip: 'bg-teal-100/60',    cta: 'bg-teal-600 hover:bg-teal-700' },
  finance:       { bg: 'bg-sky-50',     ring: 'ring-sky-200/60',     text: 'text-sky-950',     chip: 'bg-sky-100/60',     cta: 'bg-sky-600 hover:bg-sky-700' },
};

// Map the many real industry slugs onto our finite palette set.
const INDUSTRY_ALIAS = {
  it_services: 'technology', saas: 'technology', software: 'technology',
  accounting: 'professional', legal: 'professional', finance: 'finance',
  financial: 'finance', accommodation: 'hospitality', gaming: 'hospitality',
  entertainment: 'hospitality', tourism: 'hospitality', travel: 'hospitality',
  wholesale: 'retail', ecommerce: 'retail', realestate: 'property',
  trades: 'construction', logistics: 'manufacturing', agriculture: 'manufacturing',
};

const _theme = (industry) => {
  const key = (industry || '').toLowerCase();
  const resolved = INDUSTRY_THEME[key] ? key : (INDUSTRY_ALIAS[key] || 'technology');
  return INDUSTRY_THEME[resolved] || INDUSTRY_THEME.technology;
};

const _initials = (name) => {
  if (!name) return 'C';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const _greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return { label: 'Good morning', Icon: Sunrise };
  if (h < 17) return { label: 'Good afternoon', Icon: Sun };
  return { label: 'Good evening', Icon: Sunset };
};

export const UnifiedDashboardHero = ({
  userName,
  businessName,
  industry = 'technology',
  signals = [],
  cta,
  connectedSources = [],
  onRefresh,
  onAddTask,
}) => {
  const theme = useMemo(() => _theme(industry), [industry]);
  const industryConfig = getIndustryConfig(industry);
  const IndustryIcon = industryConfig?.icon;
  const { label: greetLabel } = _greeting();
  // Greeting must use the person's own first name — never the business name or
  // its initials (guards against corrupted provisioning data). Falls back to a
  // neutral greeting with no name (no emoji) when no valid personal name exists.
  const greetName = resolveGreetingName(userName, businessName);
  const hasData = Array.isArray(connectedSources) && connectedSources.length > 0;

  return (
    <div
      className={`relative overflow-hidden rounded-2xl ${theme.bg} ring-1 ring-inset ${theme.ring} ${theme.text} p-5 sm:p-7 transition-colors duration-300 ${theme.dark ? 'shadow-[0_24px_70px_-20px_rgba(150,12,38,0.7)]' : ''}`}
      data-testid="unified-dashboard-hero"
    >
      {theme.dark ? (
        <>
          {/* Ruby glass: glossy top sheen + specular highlight + deep vignette */}
          <div className="absolute inset-0 bg-[linear-gradient(150deg,rgba(255,255,255,0.28),rgba(255,255,255,0.05)_34%,transparent_60%)] pointer-events-none" />
          <div className="absolute -top-24 -right-16 w-96 h-96 rounded-full bg-[radial-gradient(circle,rgba(255,120,150,0.5),transparent_60%)] blur-2xl pointer-events-none" />
          <div className="absolute -bottom-32 -left-10 w-96 h-96 rounded-full bg-[radial-gradient(circle,rgba(90,0,20,0.6),transparent_60%)] blur-3xl pointer-events-none" />
          <div className="absolute inset-x-0 top-0 h-px bg-white/25 pointer-events-none" />
        </>
      ) : (
        /* Soft top-right glow to break flatness without being confronting */
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.85),transparent_55%)] pointer-events-none" />
      )}

      <div className="relative z-10">
        {/* Top row — identity + actions */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5">
          <div className="flex items-center gap-4 min-w-0">
            <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-white/70 backdrop-blur-md border border-white/70 shadow-sm flex items-center justify-center text-lg sm:text-xl font-bold shrink-0">
              {greetName ? greetName.slice(0, 2).toUpperCase() : _initials(businessName)}
            </div>
            <div className="min-w-0">
              <h1
                className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight break-words"
                data-testid="hero-greeting"
              >
                {greetLabel}{greetName ? `, ${greetName}` : ''}
              </h1>
              <p
                className="text-base sm:text-lg font-medium opacity-90 mt-0.5 flex items-center gap-2 truncate"
                data-testid="hero-business-name"
              >
                {IndustryIcon && <IndustryIcon className="w-4 h-4 sm:w-5 sm:h-5 opacity-80" />}
                <span className="truncate">{businessName}</span>
              </p>
              {/* Data status / empty-state hint */}
              <div className="flex items-center gap-1.5 text-xs font-medium mt-1 opacity-70" data-testid="hero-data-status">
                {hasData ? (
                  <><Wifi className="w-3 h-3 text-emerald-600" /> Live data connected</>
                ) : (
                  <span className="flex items-center gap-1.5" data-testid="hero-empty-state">
                    <WifiOff className="w-3 h-3" /> No data source connected
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Quick actions */}
          <div className="flex items-center gap-2 shrink-0" data-testid="hero-quick-actions">
            <NotificationCenter />
            <DashboardCustomizationTrigger className={theme.dark ? '!bg-white/20 !text-white !border-white/40 hover:!bg-white/30 backdrop-blur' : ''} />
            <Button
              variant="outline"
              size="sm"
              onClick={onRefresh}
              className={theme.dark ? 'bg-white/20 text-white border-white/40 hover:bg-white/30 hover:text-white backdrop-blur' : 'bg-white/60 border-white/70 hover:bg-white'}
              data-testid="hero-refresh-btn"
            >
              <RefreshCcw className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button
              size="sm"
              onClick={onAddTask}
              className={`${theme.cta} text-white border-0 shadow-sm`}
              data-testid="hero-add-task-btn"
            >
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Add Task</span>
            </Button>
          </div>
        </div>

        {/* Bottom row — signal chips + adaptive CTA */}
        {((signals && signals.length > 0) || cta) && (
          <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {(signals || []).slice(0, 3).map((s, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full ${theme.chip} border border-white/50 backdrop-blur-sm shadow-sm text-sm font-semibold`}
                  data-testid={`hero-signal-chip-${i}`}
                >
                  {s.icon ? <s.icon className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
                  <span className="truncate">{s.label}</span>
                  {s.value != null && <span className="tabular-nums ml-1">{s.value}</span>}
                </div>
              ))}
            </div>
            {cta && (
              <Button
                onClick={cta.onClick}
                size="sm"
                className={`${theme.cta} text-white border-0 shadow-sm sm:ml-auto`}
                data-testid="hero-adaptive-cta"
              >
                {cta.label || 'Jump in'}
                <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UnifiedDashboardHero;
